import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, postRetry } from "../lib/api";
import { sfx } from "../lib/sound";
import { stopNarration } from "../lib/narration";
import { mergeMap, playable } from "../lib/console";
import { ReaderLine } from "../components/kiosk/ReaderLine";
import { LabHeader, toggleFullscreen } from "../components/kiosk/LabHeader";
import { ScanningBay } from "../components/kiosk/ScanningBay";
import { CaseFile } from "../components/kiosk/CaseFile";
import { ProgressMeter } from "../components/kiosk/ProgressMeter";
import { IdentityGauge } from "../components/kiosk/IdentityGauge";
import { FlashReveal } from "../components/kiosk/FlashReveal";
import { UnlockOverlay } from "../components/kiosk/UnlockOverlay";
import { IntroOverlay } from "../components/kiosk/IntroOverlay";
import { LabNote } from "../components/kiosk/LabNote";
import { ExperimentLog } from "../components/kiosk/ExperimentLog";
import { CalibrationBanner } from "../components/kiosk/CalibrationBanner";
import { IdleWarning } from "../components/kiosk/IdleWarning";
import { OperatorPanel } from "../components/kiosk/OperatorPanel";
import { SimToolbar } from "../components/kiosk/SimToolbar";
import { MemoryLock } from "../components/kiosk/MemoryLock";
import { pickKind } from "../lib/lock";
import { useIdleTimer } from "../hooks/useIdleTimer";
import { useOperatorKeys } from "../hooks/useOperatorKeys";
import { useAudioCache } from "../hooks/useAudioCache";

const MILESTONES = [25, 50, 75];
const GAME_POLL_MS = 15000;

export default function Kiosk() {
  const [game, setGame] = useState(null);
  const [linkDown, setLinkDown] = useState(false);
  const [status, setStatus] = useState("idle");
  const [lastScan, setLastScan] = useState("");
  const [reveal, setReveal] = useState(null);
  const [lock, setLock] = useState(null);
  const [forceTick, setForceTick] = useState(0);
  const [confirmTick, setConfirmTick] = useState(0);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  const [introPhase, setIntroPhase] = useState("standby");
  const [note, setNote] = useState(null);
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [lastRead, setLastRead] = useState(null);
  const [log, setLog] = useState([]);
  const pendingUnlock = useRef(false);
  const errTimer = useRef(null);
  const dupe = useRef({ uid: "", at: 0 });
  const booted = useRef(false);
  const notesShown = useRef(new Set());
  const gameRef = useRef(null);
  const lastSpecimen = useRef(null);
  gameRef.current = game;

  const addLog = useCallback((kind, text) => setLog((l) => [{ at: Date.now(), kind, text }, ...l].slice(0, 8)), []);

  const load = useCallback(() => api.get("/game", { timeout: 12000 }).then((r) => {
    setGame(r.data);
    setLinkDown(false);
    if (!booted.current) {
      booted.current = true;
      setIntroOpen(r.data.total > 0 && r.data.decoded.length === 0);
    }
  }).catch(() => setLinkDown(true)), []);

  useEffect(() => {
    load();
    const t = setInterval(load, GAME_POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const audio = useAudioCache(game);

  const lockRef = useRef(null);
  lockRef.current = lock;

  const applyScan = useCallback((data, uid) => {
    setGame((g) => (g ? { ...g, decoded: data.decoded, total: data.total, complete: data.complete, final_message: data.final_message, subject: data.subject } : g));
    const s = data.specimen;
    lastSpecimen.current = s;
    data.status === "decoded" ? sfx.reveal() : sfx.known();
    addLog(data.status, `${s.name.toUpperCase()} -> memory "${s.memory?.title || s.spore_word}" ${data.status === "decoded" ? "recovered · clue filed" : "replayed"}`);
    setReveal({ specimen: s, status: data.status, at: Date.now() });
    if (data.status === "decoded" && data.complete) pendingUnlock.current = true;
  }, [addLog]);

  const handleScan = useCallback(async (uid) => {
    const now = Date.now();
    if (dupe.current.uid === uid && now - dupe.current.at < 1200) return;
    dupe.current = { uid, at: now };
    if (lockRef.current) { toast("Clarify the current memory first", { description: "The hive is still locating it." }); return; }
    setIntroOpen(false);
    setIntroPhase("standby");
    stopNarration();
    setNote(null);
    sfx.scan();
    setStatus("scanning");
    setLastScan(uid);
    try {
      const probe = (await postRetry("/scan", { uid, commit: false })).data;
      setLinkDown(false);
      const result = probe.status === "unknown" ? "NOT LINKED" : `${probe.specimen.name.toUpperCase()} (${probe.status === "decoded" ? "NEW" : "KNOWN"})`;
      setLastRead({ uid: probe.uid, result, at: Date.now() });
      if (probe.status === "unknown") {
        sfx.error();
        setStatus("unknown");
        addLog("unknown", `Offered unregistered object ${uid} - the hive holds nothing for it`);
        clearTimeout(errTimer.current);
        errTimer.current = setTimeout(() => setStatus("idle"), 4000);
        return;
      }
      setStatus("idle");
      const g = gameRef.current;
      const hasDial = playable(mergeMap(g?.console_map), "dial").length > 0;
      const kind = probe.status === "decoded" ? pickKind(g?.challenges, probe.specimen, probe.decoded.length, hasDial) : null;
      if (kind) {
        const decodedSpecimens = (g?.specimens || []).filter((s) => probe.decoded.includes(s.id));
        addLog("system", `${probe.specimen.name.toUpperCase()} offered - the hive cannot place the memory; ${kind === "sequence" ? "a path must be repeated" : "a fragment must be recalled"}`);
        setLock({ specimen: probe.specimen, uid, kind, decodedCount: probe.decoded.length, decodedSpecimens, at: Date.now() });
        return;
      }
      const data = probe.status === "decoded" ? (await postRetry("/scan", { uid })).data : probe;
      applyScan(data, uid);
    } catch (e) {
      setStatus("idle");
      if (!e.response) setLinkDown(true);
      setLastRead({ uid, result: "SERVER ERROR", at: Date.now() });
      toast.error("Sensor link lost. Try again.");
    }
  }, [addLog, applyScan]);

  const unlocked = useCallback(async ({ gaveIn }) => {
    const l = lockRef.current;
    setLock(null);
    if (!l) return;
    addLog("system", gaveIn ? `The hive gave in - ${l.specimen.name.toUpperCase()} released without clarification` : `${l.specimen.name.toUpperCase()} clarified on the console`);
    try {
      const data = (await postRetry("/scan", { uid: l.uid }, { tries: 3 })).data;
      if (data.status === "unknown") return;
      applyScan({ ...data, status: "decoded" }, l.uid);
    } catch (e) {
      toast.error("Sensor link lost. Scan the object again.");
    }
  }, [addLog, applyScan]);

  const closeReveal = useCallback(() => {
    setReveal(null);
    if (pendingUnlock.current) {
      pendingUnlock.current = false;
      sfx.unlock();
      setUnlockOpen(true);
      return;
    }
    const g = gameRef.current;
    if (!g || !g.total || g.complete) return;
    const pct = (g.decoded.length / g.total) * 100;
    const crossed = MILESTONES.filter((m) => pct >= m && !notesShown.current.has(m));
    if (!crossed.length) return;
    crossed.forEach((m) => notesShown.current.add(m));
    const m = crossed[crossed.length - 1];
    const text = g.narration?.[`note_${m}`];
    if (text) {
      addLog("system", `The Scientist filed a case note at ${m}% of the sequence`);
      setNote({ part: `note_${m}`, pct: m, text });
    }
  }, [addLog]);

  const closeNote = useCallback(() => setNote(null), []);

  const freshSession = (data) => {
    pendingUnlock.current = false;
    notesShown.current = new Set();
    stopNarration();
    setNote(null);
    setUnlockOpen(false);
    setReveal(null);
    setLock(null);
    setLog([]);
    setStatus("idle");
    if (data) setGame(data);
    setIntroPhase("standby");
    setIntroOpen((data || gameRef.current)?.total > 0);
  };

  const reset = async () => {
    const { data } = await api.post("/game/reset");
    freshSession(data);
    toast.success("Reset to standby. Case file cleared for the next group.");
  };

  const goIdle = useCallback(async () => {
    let data = null;
    if (gameRef.current?.decoded.length) {
      data = (await api.post("/game/reset").catch(() => ({ data: null }))).data;
    }
    freshSession(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const idleMs = (game?.idle_minutes || 3) * 60000;
  const remaining = useIdleTimer(Boolean(game) && game.total > 0 && !introOpen, idleMs, goIdle);
  const idleSeconds = Math.ceil(remaining / 1000);

  const stateRef = useRef({});
  stateRef.current = { introOpen, introPhase, reveal, note, unlockOpen, operatorOpen, lock };
  const closeIntro = useCallback(() => { setIntroOpen(false); setIntroPhase("standby"); }, []);
  useOperatorKeys({
    enabled: Boolean(game),
    onEnter: () => {
      const s = stateRef.current;
      if (s.operatorOpen) return;
      if (s.lock) setConfirmTick((t) => t + 1);
      else if (s.reveal) closeReveal();
      else if (s.note) setNote(null);
      else if (s.introOpen) { if (s.introPhase === "standby") setIntroPhase("briefing"); else closeIntro(); }
      else if (s.unlockOpen) setUnlockOpen(false);
    },
    onEscape: () => { if (stateRef.current.operatorOpen) setOperatorOpen(false); else reset(); },
    onMenu: () => setOperatorOpen((v) => !v),
    onFullscreen: toggleFullscreen,
    onReplay: () => { const s = lastSpecimen.current; if (s && !stateRef.current.reveal && !stateRef.current.lock) setReveal({ specimen: s, status: "review", at: Date.now() }); },
    onUnlock: () => { if (stateRef.current.lock) setForceTick((t) => t + 1); },
  });

  if (!game) {
    return <div data-testid="kiosk-loading" className="lab-screen flex items-center justify-center font-mono text-cyan-300 tracking-[0.3em]">BOOTING TERMINAL...</div>;
  }

  return (
    <div data-testid="kiosk-page" className="lab-screen">
      <div className="relative z-10 mx-auto flex max-w-[1600px] flex-col gap-6 px-5 py-6 sm:px-10 sm:py-8">
        <LabHeader title={game.title} decoded={game.decoded.length} total={game.total} sealed={game.sealed} audio={audio} linkDown={linkDown} onReset={reset} onOperator={() => setOperatorOpen(true)} />
        {!game.sealed && <CalibrationBanner programmed={game.total} target={game.target_count} />}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="flex flex-col gap-6">
            <ScanningBay status={status} lastScan={lastScan} />
            <ReaderLine onScan={handleScan} enabled={!unlockOpen} lastRead={lastRead} />
            <IdentityGauge decoded={game.decoded.length} total={game.total} complete={game.complete} />
            <ProgressMeter decoded={game.decoded.length} total={game.total} />
            <ExperimentLog entries={log} />
            {game.complete && !unlockOpen && (
              <button data-testid="reopen-unlock-button" onClick={() => setUnlockOpen(true)} className="lab-btn-primary justify-center">
                Replay the conclusion
              </button>
            )}
          </div>
          <CaseFile
            specimens={game.specimens}
            decoded={game.decoded}
            freshId={reveal?.status === "decoded" ? reveal.specimen.id : null}
            subject={game.subject}
            onOpen={(s) => setReveal({ specimen: s, status: "review", at: Date.now() })}
          />
        </div>
      </div>
      <FlashReveal reveal={reveal} onClose={closeReveal} />
      {lock && (
        <MemoryLock
          lock={lock}
          challenges={game.challenges}
          consoleMap={game.console_map}
          consoleDevices={game.console_devices}
          forceTick={forceTick}
          confirmTick={confirmTick}
          onUnlocked={unlocked}
        />
      )}
      <UnlockOverlay
        open={unlockOpen}
        message={game.final_message}
        finale={game.narration?.finale}
        subject={game.subject}
        specimens={game.specimens}
        onReset={reset}
        onClose={() => setUnlockOpen(false)}
      />
      <IntroOverlay
        open={introOpen && !unlockOpen && !reveal && !lock}
        phase={introPhase}
        title={game.title}
        total={game.total}
        briefing={game.narration?.briefing}
        onBegin={() => setIntroPhase("briefing")}
        onFinish={closeIntro}
      />
      {!reveal && !unlockOpen && !lock && <LabNote note={note} onClose={closeNote} />}
      {!introOpen && idleSeconds <= 30 && <IdleWarning seconds={idleSeconds} />}
      <OperatorPanel open={operatorOpen} onClose={() => setOperatorOpen(false)} game={game} onReset={reset} />
      <SimToolbar onScan={handleScan} consoleMap={game.console_map} />
    </div>
  );
}
